import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { makeStyles, Typography } from '@material-ui/core';
import i18n from 'i18n-js';

import { theme } from 'config/theme';
import { EditorTable } from '../context';
import { useTableMapWizard } from '../context';
import MapEditorCanvas, {
    ROOM,
    ZOOM_MIN,
    ZOOM_MAX,
    ZOOM_STEP,
} from '../components/MapEditorCanvas';
import TableFigure from '../components/TableFigure';
import TableSettingsPanel from '../components/TableSettingsPanel';
import { TableMaps } from 'api/controllers/tableMaps';

const MAGENTA = theme.palette.primary.main;
const HANDLE_SIZE = 6;
const SEL_PAD = 21;
const DRAG_THRESHOLD = 4;

const useStyles = makeStyles({
    root: {
        padding: '32px 40px',
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
    },
    heading: {
        fontSize: 16,
        fontWeight: 600,
        color: theme.palette.text.primary,
        marginBottom: 4,
    },
    body: {
        display: 'flex',
        gap: 0,
        alignItems: 'flex-start',
    },
    leftSpacer: {
        width: 272,
        flexShrink: 0,
    },
    canvasColumn: {
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
    },
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
    },
    zoomControls: {
        display: 'flex',
        alignItems: 'center',
        border: `1px solid ${theme.palette.grey['300']}`,
        borderRadius: 4,
        overflow: 'hidden',
    },
    zoomButton: {
        width: 32,
        height: 32,
        border: 'none',
        background: 'white',
        cursor: 'pointer',
        fontSize: 18,
        fontWeight: 600,
        color: theme.palette.text.secondary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '&:hover': {
            backgroundColor: theme.palette.grey['300'],
        },
        '&:disabled': {
            color: theme.palette.grey['400'],
            cursor: 'not-allowed',
        },
    },
    zoomLabel: {
        minWidth: 48,
        textAlign: 'center' as const,
        fontSize: 13,
        fontWeight: 600,
        color: theme.palette.text.secondary,
        borderLeft: `1px solid ${theme.palette.grey['300']}`,
        borderRight: `1px solid ${theme.palette.grey['300']}`,
        lineHeight: '32px',
        userSelect: 'none' as const,
    },
    canvasWrapper: {
        width: '100%',
        height: 440,
        borderRadius: '8px 0 0 8px',
        overflow: 'hidden',
        border: `1px solid ${theme.palette.grey['300']}`,
        borderRight: 'none',
        position: 'relative' as const,
    },
    panelColumn: {
        width: 272,
        flexShrink: 0,
        height: 440,
        // Push the panel down so its top aligns with canvasWrapper (toolbar 32px + gap 12px).
        marginTop: 44,
        border: `1px solid ${theme.palette.grey['300']}`,
        borderRadius: '0 8px 8px 0',
        overflow: 'hidden',
        backgroundColor: 'white',
    },
    emptyPanel: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        textAlign: 'center' as const,
    },
    emptyPanelText: {
        fontSize: 13,
        color: theme.palette.text.secondary,
        lineHeight: 1.5,
    },
});

function clientToSVGPoint(
    svg: SVGSVGElement,
    clientX: number,
    clientY: number
): { x: number; y: number } {
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    const inv = ctm.inverse();
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const svgPt = pt.matrixTransform(inv);
    return { x: svgPt.x, y: svgPt.y };
}

function clampTableCenter(
    nx: number,
    ny: number,
    nw: number,
    nh: number
): { x: number; y: number } {
    const halfW = nw / 2;
    const halfH = nh / 2;
    return {
        x: Math.min(1 - halfW, Math.max(halfW, nx)),
        y: Math.min(1 - halfH, Math.max(halfH, ny)),
    };
}

function SelectionHighlight({ figW, figH }: { figW: number; figH: number }) {
    const boxX = -figW / 2 - SEL_PAD;
    const boxY = -figH / 2 - SEL_PAD;
    const boxW = figW + SEL_PAD * 2;
    const boxH = figH + SEL_PAD * 2;

    const handles: [number, number][] = [
        [boxX, boxY],
        [boxX + boxW / 2, boxY],
        [boxX + boxW, boxY],
        [boxX, boxY + boxH / 2],
        [boxX + boxW, boxY + boxH / 2],
        [boxX, boxY + boxH],
        [boxX + boxW / 2, boxY + boxH],
        [boxX + boxW, boxY + boxH],
    ];

    return (
        <g pointerEvents="none">
            <rect
                x={boxX}
                y={boxY}
                width={boxW}
                height={boxH}
                fill="none"
                stroke={MAGENTA}
                strokeWidth={1.5}
                strokeDasharray="5 3"
                rx={3}
            />
            {handles.map(([hx, hy], idx) => (
                <rect
                    key={idx}
                    x={hx - HANDLE_SIZE / 2}
                    y={hy - HANDLE_SIZE / 2}
                    width={HANDLE_SIZE}
                    height={HANDLE_SIZE}
                    fill={MAGENTA}
                    stroke="white"
                    strokeWidth={1}
                />
            ))}
        </g>
    );
}

interface PlacedTableDetailsProps {
    table: EditorTable;
    svgRef: React.RefObject<SVGSVGElement>;
    isSelected: boolean;
    onSelect: (id: string) => void;
    onDragEnd: (id: string, x: number, y: number) => void;
}

const PlacedTableDetails = React.memo(function PlacedTableDetails({
    table,
    svgRef,
    isSelected,
    onSelect,
    onDragEnd,
}: PlacedTableDetailsProps) {
    const gRef = useRef<SVGGElement>(null);

    const dragActive = useRef(false);
    const didDrag = useRef(false);
    const dragStart = useRef({
        startNx: 0,
        startNy: 0,
        startPointerSvgX: 0,
        startPointerSvgY: 0,
    });

    const figW = table.width * ROOM.width;
    const figH = table.height * ROOM.height;
    const cx = ROOM.x + table.x * ROOM.width;
    const cy = ROOM.y + table.y * ROOM.height;

    const handlePointerDown = useCallback(
        (e: React.PointerEvent<SVGGElement>) => {
            e.stopPropagation();
            if (!svgRef.current) return;
            const gEl = e.currentTarget as SVGGElement;
            gEl.setPointerCapture(e.pointerId);
            const pt = clientToSVGPoint(svgRef.current, e.clientX, e.clientY);
            dragActive.current = true;
            didDrag.current = false;
            dragStart.current = {
                startNx: table.x,
                startNy: table.y,
                startPointerSvgX: pt.x,
                startPointerSvgY: pt.y,
            };
            gEl.style.cursor = 'grabbing';
        },
        [table.x, table.y, svgRef]
    );

    const handlePointerMove = useCallback(
        (e: React.PointerEvent<SVGGElement>) => {
            if (!dragActive.current || !svgRef.current) return;
            const pt = clientToSVGPoint(svgRef.current, e.clientX, e.clientY);
            const dsvgX = pt.x - dragStart.current.startPointerSvgX;
            const dsvgY = pt.y - dragStart.current.startPointerSvgY;

            if (!didDrag.current && Math.hypot(dsvgX, dsvgY) > DRAG_THRESHOLD) {
                didDrag.current = true;
            }

            if (didDrag.current) {
                const dnx = dsvgX / ROOM.width;
                const dny = dsvgY / ROOM.height;
                const clamped = clampTableCenter(
                    dragStart.current.startNx + dnx,
                    dragStart.current.startNy + dny,
                    table.width,
                    table.height
                );
                if (gRef.current) {
                    const newCx = ROOM.x + clamped.x * ROOM.width;
                    const newCy = ROOM.y + clamped.y * ROOM.height;
                    gRef.current.setAttribute('transform', `translate(${newCx}, ${newCy})`);
                }
            }
        },
        [svgRef, table.width, table.height]
    );

    const handlePointerUp = useCallback(
        (e: React.PointerEvent<SVGGElement>) => {
            if (!dragActive.current || !svgRef.current) return;
            const gEl = e.currentTarget as SVGGElement;
            gEl.style.cursor = 'grab';
            dragActive.current = false;

            if (!didDrag.current) {
                onSelect(table.id);
            } else {
                const pt = clientToSVGPoint(svgRef.current, e.clientX, e.clientY);
                const dsvgX = pt.x - dragStart.current.startPointerSvgX;
                const dsvgY = pt.y - dragStart.current.startPointerSvgY;
                const dnx = dsvgX / ROOM.width;
                const dny = dsvgY / ROOM.height;
                const clamped = clampTableCenter(
                    dragStart.current.startNx + dnx,
                    dragStart.current.startNy + dny,
                    table.width,
                    table.height
                );
                onDragEnd(table.id, clamped.x, clamped.y);
            }
        },
        [svgRef, table.id, table.width, table.height, onSelect, onDragEnd]
    );

    return (
        <g
            ref={gRef}
            transform={`translate(${cx}, ${cy})`}
            style={{ cursor: 'grab' }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
        >
            <TableFigure
                width={figW}
                height={figH}
                seats={table.seats}
                shape={table.shape}
                label={table.label}
                subLabel={String(table.seats)}
            />
            {isSelected && <SelectionHighlight figW={figW} figH={figH} />}
        </g>
    );
});

export default function TableDetailsStep() {
    const classes = useStyles();
    const { draft, dispatch } = useTableMapWizard();

    const [zoom, setZoom] = useState(1.0);
    const svgRef = useRef<SVGSVGElement>(null);
    const [selectedTableId, setSelectedTableId] = useState<string | null>(null);
    const [qrCodes, setQrCodes] = useState<string[]>([]);

    useEffect(() => {
        TableMaps.getDineInQrCodes().then(setQrCodes);
    }, []);

    useEffect(() => {
        if (selectedTableId !== null) {
            const still = draft.tables.some((t) => t.id === selectedTableId);
            if (!still) setSelectedTableId(null);
        }
    }, [draft.tables, selectedTableId]);

    const selectedTable = selectedTableId
        ? draft.tables.find((t) => t.id === selectedTableId) ?? null
        : null;

    const otherUsedQrCodes = useMemo(
        () =>
            draft.tables
                .filter((t) => t.id !== selectedTableId && !!t.qrCode)
                .map((t) => t.qrCode as string),
        [draft.tables, selectedTableId]
    );

    const handleSelect = useCallback((id: string) => {
        setSelectedTableId(id);
    }, []);

    const handleDragEnd = useCallback(
        (id: string, x: number, y: number) => {
            dispatch({ type: 'UPDATE_TABLE_POSITION', payload: { id, x, y } });
        },
        [dispatch]
    );

    const handleSave = useCallback(
        (patch: Partial<Omit<EditorTable, 'id'>>) => {
            if (!selectedTableId) return;
            dispatch({ type: 'UPDATE_TABLE', payload: { id: selectedTableId, ...patch } });
            setSelectedTableId(null);
        },
        [dispatch, selectedTableId]
    );

    const handleDelete = useCallback(() => {
        if (!selectedTableId) return;
        dispatch({ type: 'REMOVE_TABLE', payload: { id: selectedTableId } });
        setSelectedTableId(null);
    }, [dispatch, selectedTableId]);

    function handleZoomIn() {
        setZoom((prev) => Math.min(ZOOM_MAX, parseFloat((prev + ZOOM_STEP).toFixed(2))));
    }

    function handleZoomOut() {
        setZoom((prev) => Math.max(ZOOM_MIN, parseFloat((prev - ZOOM_STEP).toFixed(2))));
    }

    const zoomPercent = Math.round(zoom * 100);

    const tablesLayer = useMemo(
        () =>
            draft.tables.map((table) => (
                <PlacedTableDetails
                    key={table.id}
                    table={table}
                    svgRef={svgRef}
                    isSelected={table.id === selectedTableId}
                    onSelect={handleSelect}
                    onDragEnd={handleDragEnd}
                />
            )),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [draft.tables, selectedTableId, handleSelect, handleDragEnd]
    );

    return (
        <div className={classes.root}>
            <Typography className={classes.heading}>
                {i18n.t('tableMaps.wizard.tableDetails')}
            </Typography>

            <div className={classes.body}>
                <div className={classes.leftSpacer} />
                <div className={classes.canvasColumn}>
                    <div className={classes.toolbar}>
                        <div className={classes.zoomControls}>
                            <button
                                className={classes.zoomButton}
                                onClick={handleZoomOut}
                                disabled={zoom <= ZOOM_MIN}
                                type="button"
                                aria-label="Zoom out"
                            >
                                −
                            </button>
                            <span className={classes.zoomLabel}>{zoomPercent}%</span>
                            <button
                                className={classes.zoomButton}
                                onClick={handleZoomIn}
                                disabled={zoom >= ZOOM_MAX}
                                type="button"
                                aria-label="Zoom in"
                            >
                                +
                            </button>
                        </div>
                    </div>

                    <div className={classes.canvasWrapper}>
                        <MapEditorCanvas
                            gridEnabled={draft.gridEnabled}
                            zoom={zoom}
                            entrance={draft.entrance}
                            isMarkingEntrance={false}
                            onSelectWall={() => undefined}
                            onMoveEntrance={() => undefined}
                            svgRef={svgRef}
                        >
                            <rect
                                x={ROOM.x}
                                y={ROOM.y}
                                width={ROOM.width}
                                height={ROOM.height}
                                fill="transparent"
                                onPointerDown={() => {
                                    setSelectedTableId(null);
                                }}
                            />
                            {tablesLayer}
                        </MapEditorCanvas>
                    </div>
                </div>

                <div className={classes.panelColumn}>
                    {selectedTable ? (
                        <TableSettingsPanel
                            table={selectedTable}
                            qrCodes={qrCodes}
                            otherUsedQrCodes={otherUsedQrCodes}
                            onSave={handleSave}
                            onDelete={handleDelete}
                        />
                    ) : (
                        <div className={classes.emptyPanel}>
                            <Typography className={classes.emptyPanelText}>
                                {i18n.t('tableMaps.wizard.selectTableHint')}
                            </Typography>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
